import React, { useMemo } from 'react';
import {
  ProfileWrapper,
  ProfileTabHeader,
  ProfileTabsHeaderWrapper,
  ProfileTabContent,
  ProfileTabsContentWrapper,
  InvestorsProfileTabsHeaderWrapper,
} from './styled';
import { Tabs } from 'ds/components/tabs';
import { useLocale } from 'locale';
import { TextLabel } from 'ds/components/typography';
import { CommutePopupForm } from './CommutePopupForm';
import { ProfileSettings } from './ProfileSettings';
import { User, CommutePreference } from 'utils/entities';
import { IGenerateEmailVerificationCodeVariables, IUpdateUserProfileVariables, MutationType } from 'store/sagas/apiService/types';
import { Route, useRoute } from 'config/routes';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import { IMutationResponse } from 'store/state/mutationsResponse/selectors';
import { AuthenticationModalType } from 'components/authentication/types';
import { IReasonState } from './types';
import { InvestorsPreferencesTab } from './InvestorsPreferences';


export enum ProfileTab {
  Settings = 'settings',
  Personal = 'personal',
  Preferences = 'preferences',
}

interface ProfileProps {
  onUserDelete: (data: IReasonState) => void;
  onUpdateUser: (data: IUpdateUserProfileVariables, key: string) => void;
  user: User;
  isAgent: boolean;
  deregisterUserResponse: IMutationResponse<MutationType.DeregisterUser>;
  onSetActiveModal: (nextType: AuthenticationModalType) => void;
  generateEmailVerificationCode: (data: IGenerateEmailVerificationCodeVariables) => void;
  setEmail: (email: string) => void;
}

export const Profile: React.FC<ProfileProps> = ({
  onUpdateUser,
  onSetActiveModal,
  generateEmailVerificationCode,
  setEmail,
  deregisterUserResponse,
  user,
  onUserDelete,
  isAgent,
}) => {
  const { route, router } = useRoute();
  const { t } = useLocale();
  const screen = useScreenBreakpoint();
  const isDesktop = screen >= 3;
  const isInvestors = route.name === Route.InvestorsProfilePage;

  const safeUser: User = useMemo(() => ({
    commutePreference: null,
    ...(user || {}),
    firstName: (user || {}).firstName || '',
    phone: (user || {}).phone || '',
    email: (user || {}).email || '',
  }), [ user ]);

  const onChangePassword = () => {
    if (user.email) {
      setEmail(user.email);
      generateEmailVerificationCode({ email: user.email });
      onSetActiveModal(AuthenticationModalType.UserEmailVerification);
    }
  };

  const handleSaveCommute = (commutePreference: CommutePreference) => {
    onUpdateUser({
      commutePreference,
    }, ProfileTab.Personal);
  };

  const handleSaveChanges = (updatedUserFields: User) => {
    onUpdateUser({
      ...updatedUserFields,
    }, ProfileTab.Settings);
  };

  const renderTabContent = (tab: ProfileTab) => {
    switch (tab) {
      case ProfileTab.Personal:
        return (
          <CommutePopupForm
            onSaveCommute={handleSaveCommute}
            user={safeUser}
          />
        );
      case ProfileTab.Settings:
        return (
          <ProfileSettings
            isAgent={isAgent}
            user={safeUser}
            deregisterUserIsLoading={deregisterUserResponse && deregisterUserResponse.loading}
            onUserDelete={onUserDelete}
            onSaveChanges={handleSaveChanges}
            onChangePassword={onChangePassword}
          />
        );
      case ProfileTab.Preferences:
        return (
          <InvestorsPreferencesTab />
        );
    }
  };
  const tabs = useMemo(() => [ ProfileTab.Settings, isInvestors ? ProfileTab.Preferences : null, (isInvestors && !isDesktop) ? null : ProfileTab.Personal ].filter(Boolean), [ isInvestors, isDesktop ]);

  const activeTab = route.params.initialTab || ProfileTab.Settings;
  const setActiveTab = (tab: ProfileTab) => {
    router.navigate(route.name, {
      ...route.params,
      initialTab: tab,
    }, { replace: true });
  };

  const bodyWrapper: React.FC = ({ children }) => (
    <ProfileTabsContentWrapper isPreferences={activeTab === ProfileTab.Preferences}>
      {children}
    </ProfileTabsContentWrapper>
  );

  return (
    <ProfileWrapper>
      <Tabs
        activeTab={tabs.indexOf(activeTab)}
        setActiveTab={(nextTabIdx: number) => setActiveTab(tabs[nextTabIdx])}
        headerWrapper={isInvestors ? InvestorsProfileTabsHeaderWrapper : ProfileTabsHeaderWrapper}
        bodyWrapper={bodyWrapper}
        headers={tabs.map((tab: ProfileTab) => (
          <>
            <ShowAt from={3}>
              <ProfileTabHeader isActive={activeTab === tab}>
                <TextLabel data-auto={`tab-${tab}`} caps>{t('profile.tabLabel', { tab })}</TextLabel>
              </ProfileTabHeader>
            </ShowAt>
            {!isDesktop && isInvestors ? (
              tab !== ProfileTab.Personal ? (
                <ProfileTabHeader isActive={activeTab === tab}>
                  <TextLabel data-auto={`tab-${tab}`} caps>{t('profile.tabLabel', { tab })}</TextLabel>
                </ProfileTabHeader>
              ) : null
            ) : null}
          </>
        ))}
        bodies={tabs.map((tab: ProfileTab) => (
          <ProfileTabContent key={activeTab}> {/* discard all changes when switching to another tab */}
            {renderTabContent(tab)}
          </ProfileTabContent>
        ))}
      />
    </ProfileWrapper>
  );
};

